import { useRef } from "react";
import type { Taxonomy } from "./api";
import { useForgeEvent } from "./hooks";

export const divisionName = (t: Taxonomy, slug: string | null | undefined) =>
  t.divisions.find((d) => d.slug === slug)?.name ?? slug ?? "";
export const businessUnitName = (t: Taxonomy, slug: string | null | undefined) =>
  t.businessUnits.find((b) => b.slug === slug)?.name ?? slug ?? "";

/**
 * Division, then business unit. A division's own units come first; units whose parent is not
 * recorded yet (taxonomy.json `division: null`) follow in their own group and pair with any division.
 */
export function DivisionPicker({ taxonomy, division, businessUnit, onChange, disabled }: {
  taxonomy: Taxonomy;
  division: string;
  businessUnit: string;
  onChange: (division: string, businessUnit: string) => void;
  disabled?: boolean;
}) {
  const dSel = useRef<HTMLElement>(null);
  const bSel = useRef<HTMLElement>(null);
  useForgeEvent(dSel, "change", (e) => {
    const d = String((e.target as HTMLInputElement).value ?? "");
    const bu = taxonomy.businessUnits.find((b) => b.slug === businessUnit);
    // Keep the unit if it is still valid for the new division.
    onChange(d, bu && (bu.division === null || bu.division === d) ? businessUnit : "");
  });
  useForgeEvent(bSel, "change", (e) => onChange(division, String((e.target as HTMLInputElement).value ?? "")));

  const own = taxonomy.businessUnits.filter((b) => b.division === division);
  const unmapped = taxonomy.businessUnits.filter((b) => b.division === null);
  return (
    <>
      <forge-select label-position="block-start" ref={dSel} label="Division" value={division} required disabled={disabled || undefined}>
        {taxonomy.divisions.map((d) => <forge-option key={d.slug} value={d.slug}>{d.name}</forge-option>)}
      </forge-select>
      <forge-select label-position="block-start" ref={bSel} label="Business unit" value={businessUnit} required
        disabled={disabled || !division || undefined}>
        {own.length > 0 && (
          <forge-option-group label={divisionName(taxonomy, division)}>
            {own.map((b) => <forge-option key={b.slug} value={b.slug}>{b.name}</forge-option>)}
          </forge-option-group>
        )}
        <forge-option-group label={own.length ? "Other business units" : "Business units"}>
          {unmapped.map((b) => <forge-option key={b.slug} value={b.slug}>{b.name}</forge-option>)}
        </forge-option-group>
      </forge-select>
    </>
  );
}
